"use strict";

exports.__esModule = true;
exports.usePDFViewer = void 0;

var _axios = _interopRequireDefault(require("axios"));

var _react = require("react");

var _PDFViewerOptions = require("./PDFViewerOptions");

var _helpers = require("./helpers");

var _reducer = require("./reducer");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

const useWindowSize = () => {
  const [size, setSize] = (0, _react.useState)([0, 0]);
  (0, _react.useLayoutEffect)(() => {
    const updateSize = () => {
      setSize([window.innerWidth, window.innerHeight]);
    };

    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  return size;
};

const usePDFViewer = ({
  file,
  filename,
  options,
  scrollRef,
  setTimeout
}) => {
  /**
   * set the PDF viewer options merging the options prop with the default options
   */
  const viewerOptions = { ..._PDFViewerOptions.DEFAULT_OPTIONS,
    ...options
  };
  const {
    httpHeaders,
    maxNumberOfPages,
    loadingLabel,
    renderingLabel
  } = viewerOptions;
  const [state, dispatch] = (0, _react.useReducer)(_reducer.pdfViewerReducer, _reducer.initialState);
  const rootRef = (0, _react.useRef)();
  const mountedRef = (0, _react.useRef)(false);
  const resizeEventRef = (0, _react.useRef)();
  const pageOffsetsRef = (0, _react.useRef)(null);
  const windowSize = useWindowSize();
  const {
    blob,
    objectURL,
    ready,
    error,
    loaded,
    progress,
    currentPage,
    numPages,
    pagesRendered,
    pdfDocument,
    isMobile,
    fullScreen,
    zoom,
    previousZoom
  } = state;
  /**
   * handle component mounting lifecycle
   */

  (0, _react.useEffect)(() => {
    mountedRef.current = true;
    dispatch({
      type: _reducer.RESET_STATE
    });
    return () => {
      mountedRef.current = false;
    };
  }, []);
  /**
   * handle document resizing to calculate if using the mobile breakpoint
   */

  (0, _react.useEffect)(() => {
    clearTimeout(resizeEventRef.current);
    resizeEventRef.current = setTimeout(() => {
      const isMobileBP = (0, _helpers.isMobileBreakpoint)();

      if (isMobile !== isMobileBP) {
        dispatch({
          type: _reducer.SET_IS_MOBILE,
          payload: isMobileBP
        });
      }
    }, 1000);
    updatePageOffsets();
  }, [windowSize, setTimeout, dispatch]);
  /**
   * re-calculate the page offsets when switching to or from mobile breakpoint
   */

  (0, _react.useEffect)(() => {
    updatePageOffsets();
  }, [isMobile]);
  /**
   * fetch the document from the provided url and update the progress during download
   */

  (0, _react.useEffect)(() => {
    try {
      if (blob) {
        window.URL.revokeObjectURL(blob);
      } // reset the state when loading a new document


      dispatch({
        type: _reducer.RESET_STATE
      }); // if the file prop is a Blob then set the state immediately

      if (file instanceof Blob) {
        dispatch({
          type: _reducer.FILE_LOAD_SUCCESS,
          payload: {
            blob: file,
            zoom: _reducer.initialState.zoom,
            currentPage: viewerOptions.initialPage
          }
        });
        return;
      }

      const reqOptions = {
        url: file,
        method: 'GET',
        responseType: 'blob',
        headers: httpHeaders,
        onDownloadProgress: progressEvent => {
          if (progressEvent.loaded > 0 && progressEvent.total > 0) {
            // multiply by 50 as we show 50% progress for the download and 50% for the pdf renderring
            const progress = progressEvent.loaded / progressEvent.total * 50;

            if (mountedRef.current) {
              dispatch({
                type: _reducer.FILE_LOAD_PROGRESS,
                payload: progress
              });
            }
          }
        }
      };

      const fetchDocument = async () => {
        try {
          const response = await (0, _axios.default)(reqOptions).catch(error => {
            throw new Error('Error loading document');
          });
          const blob = response.data;

          if (mountedRef.current) {
            dispatch({
              type: _reducer.FILE_LOAD_SUCCESS,
              payload: {
                blob,
                zoom: _reducer.initialState.zoom,
                currentPage: viewerOptions.initialPage
              }
            });
          }
        } catch (error) {
          dispatch({
            type: _reducer.FILE_LOAD_ERROR
          });
        }
      };

      fetchDocument();
    } catch (error) {
      dispatch({
        type: _reducer.FILE_LOAD_ERROR
      });
    }
  }, [file]);
  /**
   * if the document has finished rendering then update the state after
   * a small delay to allow the progress animation to complete
   */

  (0, _react.useEffect)(() => {
    if (pagesRendered === numPages) {
      setTimeout(() => {
        if (!ready) {
          dispatch({
            type: _reducer.DOCUMENT_RENDERED
          });
        }
      }, 300);
    }
  }, [pagesRendered, numPages]);
  /**
   * when the document is ready calculate the initial page offsets
   */

  (0, _react.useEffect)(() => {
    if (ready) {
      setTimeout(() => {
        updatePageOffsets(); // scroll to the initial page

        const {
          initialPage
        } = viewerOptions;
        onPageNumberChange(initialPage, true);
      }, 100);
    }
  }, [ready, setTimeout]);
  /**
   * scale the page elements once the zoom state has been updated
   */

  (0, _react.useEffect)(() => {
    const stripUnits = value => parseFloat(value.toString().replace(/[^\d]/g, ''));

    const rootEl = rootRef.current;

    if (rootEl && ready) {
      const pageCanvasElements = rootEl.querySelectorAll('.react-pdf__Page__canvas');
      const {
        mobileDocumentWidth,
        desktopDocumentWidth,
        maxDocumentWidth
      } = viewerOptions;
      const breakpointScaleFactor = isMobile ? stripUnits(mobileDocumentWidth) : stripUnits(desktopDocumentWidth);
      const targetWidth = Math.min(rootEl.clientWidth * (breakpointScaleFactor / 100), stripUnits(maxDocumentWidth));
      const pageWidth = pageCanvasElements[0].getAttribute('width');
      const scaleFactor = targetWidth / pageWidth;
      const pageMargin = 15;
      let maxPageWidth = 0;

      for (let i = 0; i < pageCanvasElements.length; i++) {
        const width = pageCanvasElements[i].getAttribute('width');
        const newWidth = width * (zoom / 100) * scaleFactor;

        if (newWidth > maxPageWidth) {
          maxPageWidth = newWidth;
        }

        const newMargin = pageMargin * (zoom / 100);
        pageCanvasElements[i].style.width = `${newWidth}px`;
        pageCanvasElements[i].style.height = 'auto';
        pageCanvasElements[i].style.marginBottom = `${newMargin}px`;
      }

      const scrollTop = scrollRef.current.scrollTop;
      let scrollLeft = 0;

      if (maxPageWidth > scrollRef.current.clientWidth) {
        scrollLeft = (maxPageWidth - scrollRef.current.clientWidth) / 2;
      }

      scrollRef.current.scrollTop = scrollTop * (zoom / previousZoom);
      scrollRef.current.scrollLeft = scrollLeft;
      updatePageOffsets();
    }
  }, [zoom, previousZoom, ready]);
  /**
   * sets the pageOffsetsRef property after calculating the vertical offsets of the document pages
   * uses a ref to prevent the component re-rendering
   */

  const updatePageOffsets = () => {
    const rootEl = rootRef.current;

    if (rootEl) {
      const pageElements = rootEl.querySelectorAll('.document__page');
      const pageOffsets = [];

      for (const page of pageElements) {
        pageOffsets.push(page.offsetTop);
      }

      pageOffsetsRef.current = pageOffsets;
    }
  };
  /**
   * sets the current page and handles the document scroll event and update the page number (throttle calls to improve performance in the browser)
   * @param  {Object} scrollElement
   */


  const handleScroll = (0, _react.useCallback)(scrollElement => {
    const scrollValue = scrollElement.scrollTop;
    const containerHeight = scrollElement.clientHeight;
    const scrollThreshold = scrollValue + containerHeight / 2;
    const pageNumber = (0, _helpers.getCurrentPageFromScrollOffset)(pageOffsetsRef.current, scrollThreshold);

    if (currentPage !== pageNumber) {
      dispatch({
        type: _reducer.SET_CURRENT_PAGE,
        payload: pageNumber
      });
    }
  }, [dispatch, currentPage]);
  /**
   * event handler for processing when a single page is renderred
   */

  const onPageRendered = (0, _react.useCallback)(() => {
    dispatch({
      type: _reducer.PAGE_RENDERED
    });
  }, [pagesRendered, numPages, dispatch]);
  /**
   * event handler for when the document fully loads
   * @param {Object} Document Object provided from React PDF
   */

  const onDocumentLoaded = (0, _react.useCallback)(pdfDocument => {
    setTimeout(() => {
      dispatch({
        type: _reducer.DOCUMENT_LOADED,
        payload: pdfDocument
      });
      setTimeout(() => {
        updatePageOffsets();
      }, 500);
    }, 1000);
  }, [updatePageOffsets, setTimeout]);
  /**
   * downloads a PDF from the document blob
   */

  const download = (0, _react.useCallback)(() => {
    // it is necessary to create a new blob object with mime-type explicitly set
    // otherwise only Chrome works like it should
    var fileBlob = new Blob([blob], {
      type: 'application/pdf'
    }); // IE doesn't allow using a blob object directly as link href
    // instead it is necessary to use msSaveOrOpenBlob

    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(fileBlob, filename);
      return;
    } // in all other browsers create a hyperlink to the ObjectURL with the download attribute


    const objectURL = window.URL.createObjectURL(fileBlob);
    var link = document.createElement('a');
    link.href = objectURL;
    document.body.appendChild(link);
    link.download = filename;

    if (objectURL) {
      link.click();
    } // // for Firefox it is necessary to delay revoking the ObjectURL


    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectURL);
    }, 1000);
  }, [blob, filename, setTimeout]);

  const onPageNumberChange = (pageNumber, forceUpdate) => {
    if (currentPage !== pageNumber || forceUpdate) {
      pageNumber = pageNumber >= 1 ? pageNumber : 1;
      pageNumber = pageNumber <= numPages ? pageNumber : numPages;
      const scrollPosition = pageOffsetsRef.current[pageNumber - 1] - pageOffsetsRef.current[0];
      scrollRef.current.scrollTop = scrollPosition;
      dispatch({
        type: _reducer.SET_CURRENT_PAGE,
        payload: pageNumber
      });
    }
  };
  /**
   * callback method to update the state when the current zoom level changes
   */


  const onZoomChange = (0, _react.useCallback)(newZoom => {
    dispatch({
      type: _reducer.SET_ZOOM,
      payload: newZoom
    });
  }, [dispatch]);
  /**
   * callback method to update the state when the fullscreen mode is toggled
   */

  const onToggleFullScreen = (0, _react.useCallback)(fullScreen => {
    if (fullScreen) {
      const elem = rootRef.current;

      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        /* Safari */
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        /* IE/Edge */
        elem.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        /* Chrome, Safari and Opera */
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        /* IE/Edge */
        document.msExitFullscreen();
      }
    }

    dispatch({
      type: _reducer.SET_FULLSCREEN,
      payload: fullScreen
    });
  }, [dispatch]);
  const showDocumentPreview = loaded && numPages <= maxNumberOfPages;
  const showProgressIndicator = !ready && numPages <= maxNumberOfPages;
  const progressLabel = progress > 50 ? renderingLabel : loadingLabel;
  return {
    rootRef,
    showDocumentPreview,
    showProgressIndicator,
    progressLabel,
    progress,
    blob,
    objectURL,
    ready,
    error,
    pdfDocument,
    zoom,
    viewerOptions,
    currentPage,
    numPages,
    handleScroll,
    download,
    isMobile,
    fullScreen: !!fullScreen,
    onDocumentLoaded,
    onPageRendered,
    onPageNumberChange,
    onZoomChange,
    onToggleFullScreen
  };
};

exports.usePDFViewer = usePDFViewer;